import React, { useState } from 'react';
import { MessageCircle, X, Star, Check } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { track } from '../lib/analytics';

const DISCOVERY_OPTIONS = [
    { value: 'google', label: 'Google' },
    { value: 'ia', label: 'IA (ChatGPT, Claude...)' },
    { value: 'reseaux_sociaux', label: 'Réseaux sociaux' },
    { value: 'bouche_a_oreille', label: 'Bouche à oreille' },
    { value: 'autre', label: 'Autre' },
];

const FeedbackWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [discoverySource, setDiscoverySource] = useState(null);
    const [discoverySourceOther, setDiscoverySourceOther] = useState('');
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [bugReport, setBugReport] = useState('');
    const [recommendation, setRecommendation] = useState('');

    const { user } = useAuth();
    const location = useLocation();

    const resetForm = () => {
        setDiscoverySource(null);
        setDiscoverySourceOther('');
        setRating(0);
        setHoverRating(0);
        setBugReport('');
        setRecommendation('');
        setSubmitted(false);
    };

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(resetForm, 300);
    };

    const hasContent = discoverySource || rating > 0 || bugReport.trim() || recommendation.trim();

    const handleSubmit = async () => {
        if (!hasContent || isSubmitting) return;
        setIsSubmitting(true);

        try {
            const { error } = await supabase.from('user_feedback').insert({
                user_id: user?.id || null,
                discovery_source: discoverySource,
                discovery_source_other: discoverySource === 'autre' ? discoverySourceOther.trim() || null : null,
                rating: rating > 0 ? rating : null,
                bug_report: bugReport.trim() || null,
                recommendation: recommendation.trim() || null,
                page: location.pathname,
                user_agent: navigator.userAgent,
            });

            if (error) throw error;

            track('feedback_submitted', {
                discovery_source: discoverySource,
                rating: rating > 0 ? rating : null,
                has_bug_report: Boolean(bugReport.trim()),
                has_recommendation: Boolean(recommendation.trim()),
                page: location.pathname,
            });

            setSubmitted(true);
        } catch (err) {
            console.error('Error submitting feedback:', err);
            alert("Une erreur est survenue, réessaie plus tard.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Floating button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 flex items-center gap-2 bg-[#0F172A] text-white pl-4 pr-5 py-3 rounded-full shadow-lg shadow-slate-900/25 hover:bg-[#1e293b] active:scale-95 transition-all"
                    aria-label="Donner mon avis"
                >
                    <MessageCircle size={18} />
                    <span className="text-sm font-bold hidden sm:inline">Un avis ?</span>
                </button>
            )}

            {/* Modal */}
            {isOpen && (
                <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center sm:px-4">
                    <div
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        onClick={handleClose}
                    />

                    <div className="relative bg-white rounded-t-[32px] sm:rounded-[32px] w-full sm:max-w-md p-6 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={handleClose}
                            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                        >
                            <X size={20} />
                        </button>

                        {submitted ? (
                            <div className="text-center py-8">
                                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Check size={26} className="text-green-600" strokeWidth={3} />
                                </div>
                                <h2 className="text-xl font-black text-slate-900 mb-2">Merci !</h2>
                                <p className="text-slate-500 text-sm">
                                    Ton retour compte vraiment, on va s'en servir pour améliorer le site.
                                </p>
                                <button
                                    onClick={handleClose}
                                    className="mt-6 w-full bg-slate-100 text-slate-700 font-bold py-3 rounded-xl hover:bg-slate-200 transition-all"
                                >
                                    Fermer
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="mb-6">
                                    <h2 className="text-xl font-black text-slate-900 mb-2">
                                        Donne-nous ton avis
                                    </h2>
                                    <p className="text-slate-500 text-sm leading-relaxed">
                                        Trouve ton VIE est un side project qu'on fait évoluer avec les retours de ses utilisateurs. Deux minutes suffisent pour nous aider.
                                    </p>
                                </div>

                                <div className="space-y-6">
                                    {/* Discovery source */}
                                    <div>
                                        <label className="block text-sm font-bold text-slate-900 mb-2">
                                            Comment as-tu découvert le site ?
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {DISCOVERY_OPTIONS.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => setDiscoverySource(opt.value)}
                                                    className={`px-3 py-2 rounded-full text-xs font-bold border-2 transition-all ${discoverySource === opt.value
                                                        ? 'bg-blue-600 border-blue-600 text-white'
                                                        : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'
                                                        }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                        {discoverySource === 'autre' && (
                                            <input
                                                type="text"
                                                value={discoverySourceOther}
                                                onChange={(e) => setDiscoverySourceOther(e.target.value)}
                                                placeholder="Précise..."
                                                className="mt-2 w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400"
                                            />
                                        )}
                                    </div>

                                    {/* Rating */}
                                    <div>
                                        <label className="block text-sm font-bold text-slate-900 mb-2">
                                            Ton expérience sur le site, sur 5 ?
                                        </label>
                                        <div className="flex gap-1">
                                            {[1, 2, 3, 4, 5].map(star => (
                                                <button
                                                    key={star}
                                                    type="button"
                                                    onClick={() => setRating(star)}
                                                    onMouseEnter={() => setHoverRating(star)}
                                                    onMouseLeave={() => setHoverRating(0)}
                                                    className="p-1"
                                                    aria-label={`${star} étoiles`}
                                                >
                                                    <Star
                                                        size={28}
                                                        className={`transition-colors ${star <= (hoverRating || rating)
                                                            ? 'fill-amber-400 text-amber-400'
                                                            : 'fill-transparent text-slate-300'
                                                            }`}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Bugs */}
                                    <div>
                                        <label className="block text-sm font-bold text-slate-900 mb-2">
                                            As-tu rencontré des bugs ?
                                        </label>
                                        <textarea
                                            value={bugReport}
                                            onChange={(e) => setBugReport(e.target.value)}
                                            placeholder="Décris ce qui s'est mal passé (optionnel)"
                                            rows={2}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:border-blue-400"
                                        />
                                    </div>

                                    {/* Recommendations */}
                                    <div>
                                        <label className="block text-sm font-bold text-slate-900 mb-2">
                                            Une recommandation pour améliorer le site ?
                                        </label>
                                        <textarea
                                            value={recommendation}
                                            onChange={(e) => setRecommendation(e.target.value)}
                                            placeholder="Ton idée nous intéresse (optionnel)"
                                            rows={2}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:border-blue-400"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={handleSubmit}
                                    disabled={!hasContent || isSubmitting}
                                    className={`w-full mt-6 font-bold py-4 rounded-xl shadow-lg transition-all active:scale-[0.98] ${hasContent && !isSubmitting
                                        ? 'bg-slate-900 text-white hover:bg-blue-600'
                                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                        }`}
                                >
                                    {isSubmitting ? 'Envoi...' : 'Envoyer mon avis'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default FeedbackWidget;
